import {
  ANI_ROUTER,
  ASSET,
  CLIENT,
  DATE_RANGE_ROUTER,
  DAY_OF_WEEK_ROUTER,
  Device,
  DNIS_ROUTER,
  HUNT_GROUP,
  INBOUND_NUMBER,
  IP_AUTHENTICATION,
  MATCH_START,
  NUMBER_PLAN,
  PLAY,
  PLUGIN,
  QUEUE,
  REMOTE,
  ROUTE_START,
  SAY,
  SCRIPT,
  SIP,
  SIP_GATEWAY,
  SUB_FLOW,
  TEAM,
  TIME_RANGE_ROUTER,
  VOICEMAIL,
  WEBHOOK,
  ZONE_ROUTER,
} from "callable-data-objects";
import Devices from "Components/Device/Devices";
import InboundFlows from "Components/Device/InboundFlow/InboundFlows";
import InboundNumbers from "Components/Device/InboundNumber/InboundNumbers";
import OutboundFlows from "Components/Device/OutboundFlow/OutboundFlows";
import SubFlows from "Components/Device/SubFlow/SubFlows";
import Trunks from "Components/Device/Trunk/TrunkView";
import Webhooks from "Components/Device/Webhook/WebhookView";
import useCheckRole from "Hooks/APICheckRole";
import { useDevice } from "Hooks/Endpoints";
import APIAssetProvider from "Providers/APIAssetProvider";
import APIDDIProvider from "Providers/APIDDIProvider";
import APITrunkProvider from "Providers/APITrunkProvider";
import APIAccountWebhookProvider from "Providers/APIWebhookProvider";
import { APIDeviceProps } from "Providers/types";
import React, { Fragment } from "react";
import { Container, Grid, Label, Menu } from "semantic-ui-react";
import AssetView from "./Asset/AssetView";
import "./device.css";

const getColor = (isActive: boolean, count: number) => {
  if (isActive) return "green";
  if (count === 0) return "grey";
  return "purple";
};

type MenuItemProps = {
  title: string;
  activeMenu: string;
  name: string;
  count: number;
  onClick: Function;
};

const MenuItem = ({
  title,
  activeMenu,
  name,
  count,
  onClick,
}: MenuItemProps) => {
  const isActive = activeMenu === name;
  return (
    <Menu.Item
      name={name}
      active={isActive}
      onClick={(e, { name }) => onClick(name)}
    >
      <Label color={getColor(isActive, count)}>{count}</Label>
      {title}
    </Menu.Item>
  );
};

const DeviceView = () => {
  const { hasRole, roles } = useCheckRole();

  const {
    account,
    device,
    devices,
    select,
    activeMenu,
    onMenu,
  }: APIDeviceProps = useDevice();

  if (!account) return <Fragment />;

  const { ddis = [], trunks = [], callbackURLS = [], assets = [] } = account;

  const isActive = (name: string) => {
    return activeMenu === name;
  };

  const filter = (type: string) => {
    if (devices) {
      return devices?.filter((d: Device) => d.type === type);
    }
    return [];
  };

  const length = (type: string) => {
    return filter(type).length;
  };

  return (
    <Container fluid style={{ marginTop: "20px" }}>
      <Grid padded stackable columns={2}>
        {!device && (
          <Grid.Column width={4}>
            <Menu vertical fluid pointing className='sideMenu'>
              <Menu.Item>
                <Menu.Header>Flows</Menu.Header>

                <Menu.Menu>
                  {hasRole(roles.account.device.inbound.roles.view) && (
                    <MenuItem
                      name={ROUTE_START}
                      title='Inbound'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(ROUTE_START)}
                    />
                  )}

                  {hasRole(roles.account.device.outbound.roles.view) && (
                    <MenuItem
                      name={MATCH_START}
                      title='Outbound'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(MATCH_START)}
                    />
                  )}

                  {hasRole(roles.account.device.subflow.roles.view) && (
                    <MenuItem
                      name={SUB_FLOW}
                      title='Sub-Flows'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(SUB_FLOW)}
                    />
                  )}
                </Menu.Menu>
              </Menu.Item>

              <Menu.Item>
                <Menu.Header>Resources</Menu.Header>

                <Menu.Menu>
                  {hasRole(roles.account.device.ddi.roles.view) && (
                    <MenuItem
                      name={INBOUND_NUMBER}
                      title='Inbound Numbers'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={ddis.length}
                    />
                  )}

                  {hasRole(roles.account.device.trunk.roles.view) && (
                    <MenuItem
                      name={IP_AUTHENTICATION}
                      title='SIP Trunks'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={trunks.length}
                    />
                  )}

                  {hasRole(roles.account.device.hook.roles.view) && (
                    <MenuItem
                      name={WEBHOOK}
                      title='Webhooks'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={callbackURLS.length}
                    />
                  )}

                  {hasRole(roles.account.device.asset.roles.view) && (
                    <MenuItem
                      name={ASSET}
                      title='Assets'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={assets.length}
                    />
                  )}
                </Menu.Menu>
              </Menu.Item>

              <Menu.Item>
                <Menu.Header>Feedback</Menu.Header>

                <Menu.Menu>
                  {hasRole(roles.account.device.play.roles.view) && (
                    <MenuItem
                      name={PLAY}
                      title='Play'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(PLAY)}
                    />
                  )}

                  {hasRole(roles.account.device.say.roles.view) && (
                    <MenuItem
                      name={SAY}
                      title='Say'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(SAY)}
                    />
                  )}
                </Menu.Menu>
              </Menu.Item>

              <Menu.Item>
                <Menu.Header>Endpoints</Menu.Header>

                <Menu.Menu>
                  {hasRole(roles.account.device.remote.roles.view) && (
                    <MenuItem
                      name={REMOTE}
                      title='Remote Endpoints'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(REMOTE)}
                    />
                  )}
                  {hasRole(roles.account.device.client.roles.view) && (
                    <MenuItem
                      name={CLIENT}
                      title='WebRTC / SIP Client'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(CLIENT)}
                    />
                  )}

                  {hasRole(roles.account.device.sipExtension.roles.view) && (
                    <MenuItem
                      name={SIP}
                      title='SIP Extensions'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(SIP)}
                    />
                  )}
                  {hasRole(roles.account.device.sipGateway.roles.view) && (
                    <MenuItem
                      name={SIP_GATEWAY}
                      title='SIP Gateways'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(SIP_GATEWAY)}
                    />
                  )}
                  {hasRole(roles.account.device.teams.roles.view) && (
                    <MenuItem
                      name={TEAM}
                      title='MS Teams'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(TEAM)}
                    />
                  )}
                  {hasRole(roles.account.device.huntGroup.roles.view) && (
                    <MenuItem
                      name={HUNT_GROUP}
                      title='Hunt Groups'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(HUNT_GROUP)}
                    />
                  )}
                  {hasRole(roles.account.device.queue.roles.view) && (
                    <MenuItem
                      name={QUEUE}
                      title='Queues'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(QUEUE)}
                    />
                  )}
                  {hasRole(roles.account.device.voicemail.roles.view) && (
                    <MenuItem
                      name={VOICEMAIL}
                      title='Voicemails'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(VOICEMAIL)}
                    />
                  )}
                </Menu.Menu>
              </Menu.Item>

              <Menu.Item>
                <Menu.Header>Routing</Menu.Header>

                <Menu.Menu>
                  {hasRole(roles.account.device.aniRouter.roles.view) && (
                    <MenuItem
                      name={ANI_ROUTER}
                      title='ANI Router'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(ANI_ROUTER)}
                    />
                  )}
                  {hasRole(roles.account.device.dnisRouter.roles.view) && (
                    <MenuItem
                      name={DNIS_ROUTER}
                      title='DNIS Router'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(DNIS_ROUTER)}
                    />
                  )}
                  {hasRole(roles.account.device.regionRouter.roles.view) && (
                    <MenuItem
                      name={ZONE_ROUTER}
                      title='Region Router'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(ZONE_ROUTER)}
                    />
                  )}
                  {hasRole(roles.account.device.digits.roles.view) && (
                    <MenuItem
                      name={NUMBER_PLAN}
                      title='Digits (IVR)'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(NUMBER_PLAN)}
                    />
                  )}
                  {hasRole(roles.account.device.dayOfWeek.roles.view) && (
                    <MenuItem
                      name={DAY_OF_WEEK_ROUTER}
                      title='Day Of Week Router'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(DAY_OF_WEEK_ROUTER)}
                    />
                  )}
                  {hasRole(roles.account.device.dateRange.roles.view) && (
                    <MenuItem
                      name={DATE_RANGE_ROUTER}
                      title='Date Range Router'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(DATE_RANGE_ROUTER)}
                    />
                  )}
                  {hasRole(roles.account.device.timeRange.roles.view) && (
                    <MenuItem
                      name={TIME_RANGE_ROUTER}
                      title='Time Range Router'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(TIME_RANGE_ROUTER)}
                    />
                  )}
                  {hasRole(roles.account.device.plugin.roles.view) && (
                    <MenuItem
                      name={PLUGIN}
                      title='Plugin'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(PLUGIN)}
                    />
                  )}
                  {hasRole(roles.account.device.script.roles.view) && (
                    <MenuItem
                      name={SCRIPT}
                      title='Script'
                      activeMenu={activeMenu}
                      onClick={onMenu}
                      count={length(SCRIPT)}
                    />
                  )}
                </Menu.Menu>
              </Menu.Item>
            </Menu>
          </Grid.Column>
        )}

        <Grid.Column width={device ? 16 : 12}>
          {isActive(INBOUND_NUMBER) &&
            hasRole(roles.account.device.ddi.roles.view) && (
              <APIDDIProvider>
                <InboundNumbers />
              </APIDDIProvider>
            )}
          {isActive(IP_AUTHENTICATION) &&
            hasRole(roles.account.device.trunk.roles.view) && (
              <APITrunkProvider>
                <Trunks />
              </APITrunkProvider>
            )}
          {isActive(ASSET) && hasRole(roles.account.device.asset.roles.view) && (
            <APIAssetProvider>
              <AssetView />
            </APIAssetProvider>
          )}
          {isActive(WEBHOOK) && hasRole(roles.account.device.hook.roles.view) && (
            <APIAccountWebhookProvider>
              <Webhooks />
            </APIAccountWebhookProvider>
          )}

          {isActive(ROUTE_START) &&
            hasRole(roles.account.device.inbound.roles.view) && (
              <InboundFlows
                title='Inbound Flow'
                flows={filter(ROUTE_START)}
                ddis={ddis}
                onSelect={select}
              />
            )}
          {isActive(MATCH_START) &&
            hasRole(roles.account.device.outbound.roles.view) && (
              <OutboundFlows
                title='Outbound Flow'
                flows={filter(MATCH_START)}
                onSelect={select}
              />
            )}

          {isActive(SUB_FLOW) &&
            hasRole(roles.account.device.subflow.roles.view) && <SubFlows />}

          {isActive(PLAY) && hasRole(roles.account.device.play.roles.view) && (
            <Devices title='Play' type={PLAY} device={device} />
          )}

          {isActive(SAY) && hasRole(roles.account.device.say.roles.view) && (
            <Devices title='Say' type={SAY} device={device} />
          )}

          {isActive(REMOTE) &&
            hasRole(roles.account.device.remote.roles.view) && (
              <Devices title='Remote' type={REMOTE} device={device} />
            )}

          {isActive(CLIENT) &&
            hasRole(roles.account.device.client.roles.view) && (
              <Devices title='Client' type={CLIENT} device={device} />
            )}

          {isActive(SIP) &&
            hasRole(roles.account.device.sipExtension.roles.view) && (
              <Devices title='Extension' type={SIP} device={device} />
            )}

          {isActive(SIP_GATEWAY) &&
            hasRole(roles.account.device.sipGateway.roles.view) && (
              <Devices title='Gateway' type={SIP_GATEWAY} device={device} />
            )}

          {isActive(TEAM) && hasRole(roles.account.device.teams.roles.view) && (
            <Devices title='MS Teams' type={TEAM} device={device} />
          )}

          {isActive(HUNT_GROUP) &&
            hasRole(roles.account.device.huntGroup.roles.view) && (
              <Devices title='Hunt Group' type={HUNT_GROUP} device={device} />
            )}

          {isActive(ANI_ROUTER) &&
            hasRole(roles.account.device.aniRouter.roles.view) && (
              <Devices title='ANI Router' type={ANI_ROUTER} device={device} />
            )}

          {isActive(DNIS_ROUTER) &&
            hasRole(roles.account.device.dnisRouter.roles.view) && (
              <Devices title='DNIS Router' type={DNIS_ROUTER} device={device} />
            )}

          {isActive(ZONE_ROUTER) &&
            hasRole(roles.account.device.regionRouter.roles.view) && (
              <Devices
                title='Region Router'
                type={ZONE_ROUTER}
                device={device}
              />
            )}

          {isActive(NUMBER_PLAN) &&
            hasRole(roles.account.device.digits.roles.view) && (
              <Devices title='Digits' type={NUMBER_PLAN} device={device} />
            )}

          {isActive(DAY_OF_WEEK_ROUTER) &&
            hasRole(roles.account.device.dayOfWeek.roles.view) && (
              <Devices
                title='DOW Router'
                type={DAY_OF_WEEK_ROUTER}
                device={device}
              />
            )}

          {isActive(TIME_RANGE_ROUTER) &&
            hasRole(roles.account.device.timeRange.roles.view) && (
              <Devices
                title='Time Range Router'
                type={TIME_RANGE_ROUTER}
                device={device}
              />
            )}

          {isActive(DATE_RANGE_ROUTER) &&
            hasRole(roles.account.device.dateRange.roles.view) && (
              <Devices
                title='Date Range Router'
                type={DATE_RANGE_ROUTER}
                device={device}
              />
            )}

          {isActive(PLUGIN) &&
            hasRole(roles.account.device.plugin.roles.view) && (
              <Devices title='Plugin' type={PLUGIN} device={device} />
            )}

          {isActive(SCRIPT) &&
            hasRole(roles.account.device.script.roles.view) && (
              <Devices title='Script' type={SCRIPT} device={device} />
            )}

          {isActive(QUEUE) &&
            hasRole(roles.account.device.queue.roles.view) && (
              <Devices title='Queue' type={QUEUE} device={device} />
            )}

          {isActive(VOICEMAIL) &&
            hasRole(roles.account.device.voicemail.roles.view) && (
              <Devices title='Voicemail' type={VOICEMAIL} device={device} />
            )}
        </Grid.Column>
      </Grid>
    </Container>
  );
};

export default DeviceView;
